import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { AnalysisCampaignObject, AnalysisFiltersObject, TotalMetricsObject } from "../types/APITypes";
import { assertNotNull } from "../util/helpers/assert";
import { useGetAnalysisCampaigns } from "../hooks/useGetAnalysisCampaigns";
import { stringConverter } from "../util/functions";

type Metrics = "live" | "projections" | "delta";

interface AnalysisContextType {
    analysisCampaigns: AnalysisCampaignObject[];
    setAnalysisCampaigns: (a : AnalysisCampaignObject[]) => void;

    analysisFilters: AnalysisFiltersObject;
    setAnalysisFilters: (f: AnalysisFiltersObject) => void;

    analysisLoading: boolean;
    setAnalysisLoading: (l: boolean) => void;

    analysisRefresh: boolean;
    setAnalysisRefresh: (r: boolean) => void;

    showProjections: Metrics;
    setShowProjections: (l: Metrics) => void;

    analysisTotalMetrics: TotalMetricsObject;
    setAnalysisTotalMetrics: (t: TotalMetricsObject) => void;
}
const emptyTotals = {
    total_campaigns : 0,
    total_budget : 0,
    total_nnls : 0,
    total_mqls : 0,
    total_sqls : 0,
    total_pipe_count : 0,
    total_pipe_amount : 0,
}


const AnalysisContext = createContext<AnalysisContextType | null>(null);


export const useAnalysisContext = () => assertNotNull(
    useContext(AnalysisContext),
    "AnalysisContext.useContext must be called from a child of AnalysisContext.Provider"
)


export const Provider = ({children} : React.PropsWithChildren) => {

    const [analysisCampaigns, setAnalysisCampaigns] = useState<AnalysisCampaignObject[]>([] as AnalysisCampaignObject[]);
    const [analysisFilters, setAnalysisFilters] = useState<AnalysisFiltersObject>({} as AnalysisFiltersObject);
    const [analysisLoading, setAnalysisLoading] = useState(true);
    const [analysisRefresh, setAnalysisRefresh] = useState(false);
    const [showProjections, setShowProjections] = useState("live" as Metrics);
    const [analysisTotalMetrics, setAnalysisTotalMetrics] = useState<TotalMetricsObject>(emptyTotals);

    const analysisCampaignsResponse = useGetAnalysisCampaigns();
    let allAnalysisCampaigns = useRef<AnalysisCampaignObject[]>();

    const handleFilterChange = () => {

        let filtered = JSON.parse(JSON.stringify(allAnalysisCampaigns.current));
        let key: keyof AnalysisFiltersObject;

        for (key in analysisFilters) {
            if (analysisFilters[key].length) {
              console.log('af',key, analysisFilters[key])
                const normalizedFilterValues = key === "campaign_subregion"
                    ? analysisFilters[key].map((value) => value.toLowerCase())
                    : analysisFilters[key];
                filtered = filtered
                    .filter((a: AnalysisCampaignObject) => 
                        normalizedFilterValues.includes(
                            key === "campaign_subregion"
                                ? (a[key] ? stringConverter(a[key]).toLowerCase() : "")
                                : stringConverter(a[key])
                        )
                )
            }
        }

        setAnalysisCampaigns(filtered);
    }


    const calculateTotals = useMemo(() => {
        let totals:TotalMetricsObject = {} as TotalMetricsObject;
        switch (showProjections) {
            case "projections": {
                totals = analysisCampaigns.length ? {
                    total_campaigns : analysisCampaigns.length,
                    total_budget : analysisCampaigns.reduce((sum, c) => sum + c.budget_amount, 0),
                    total_nnls : analysisCampaigns.reduce((sum, c) => sum + c.nnl_projected_count, 0),
                    total_mqls : analysisCampaigns.reduce((sum, c) => sum + c.mql_projected_count, 0),
                    total_sqls : analysisCampaigns.reduce((sum, c) => sum + c.sql_projected_count, 0),
                    total_pipe_count : analysisCampaigns.reduce((sum, c) => sum + c.pipe_projected_count, 0),
                    total_pipe_amount : analysisCampaigns.reduce((sum, c) => sum + c.pipe_projected_amount, 0)
                } : emptyTotals;
                break;
            }
            case "delta": {
                totals = analysisCampaigns.length ? {
                    total_campaigns : analysisCampaigns.length,
                    total_budget : analysisCampaigns.reduce((sum, c) => sum + c.budget_amount, 0),
                    total_nnls : analysisCampaigns.reduce((sum, c) => sum + c.nnl_count - c.nnl_projected_count, 0),
                    total_mqls : analysisCampaigns.reduce((sum, c) => sum + c.mql_count - c.mql_projected_count, 0),
                    total_sqls : analysisCampaigns.reduce((sum, c) => sum + c.sql_count - c.sql_projected_count, 0),
                    total_pipe_count : analysisCampaigns.reduce((sum, c) => sum + c.pipe_count - c.pipe_projected_count, 0),
                    total_pipe_amount : analysisCampaigns.reduce((sum, c) => sum + c.pipe_amount - c.pipe_projected_amount, 0)
                } : emptyTotals;
                break;
            }
            default:  {
                totals = analysisCampaigns.length ? {
                    total_campaigns : analysisCampaigns.length,
                    total_budget : analysisCampaigns.reduce((sum, c) => sum + c.budget_amount, 0),
                    total_nnls : analysisCampaigns.reduce((sum, c) => sum + c.nnl_count, 0),
                    total_mqls : analysisCampaigns.reduce((sum, c) => sum + c.mql_count, 0),
                    total_sqls : analysisCampaigns.reduce((sum, c) => sum + c.sql_count, 0),
                    total_pipe_count : analysisCampaigns.reduce((sum, c) => sum + c.pipe_count, 0),
                    total_pipe_amount : analysisCampaigns.reduce((sum, c) => sum + c.pipe_amount, 0)
                } : emptyTotals;
                break;

            }
        }

        return totals
    }, [analysisCampaigns, showProjections])


    useEffect(() => {

        console.log(analysisCampaignsResponse)
        if (analysisCampaignsResponse?.value !== undefined){
            allAnalysisCampaigns.current = analysisCampaignsResponse.value;
            setAnalysisCampaigns(analysisCampaignsResponse.value);
            setAnalysisLoading(false);
        }
    }, [analysisCampaignsResponse.value])

    useEffect(() => {
        console.log(`Effect Refresh ${analysisRefresh}`)
        if (analysisRefresh) {
            handleFilterChange();
        }
        setAnalysisRefresh(false);
        setAnalysisLoading(false);
    }, [analysisRefresh])

    useEffect(() => {
        setAnalysisTotalMetrics(calculateTotals);
    }, [analysisCampaigns, analysisRefresh])


    useEffect(() => {
        setAnalysisTotalMetrics(calculateTotals);
    }, [showProjections])

    const providerValue = useMemo(() => ({
        analysisCampaigns, setAnalysisCampaigns,
        analysisFilters, setAnalysisFilters,
        analysisLoading, setAnalysisLoading,
        analysisRefresh, setAnalysisRefresh,
        showProjections, setShowProjections,
        analysisTotalMetrics, setAnalysisTotalMetrics
    }), [analysisCampaigns, analysisFilters, analysisLoading, analysisRefresh, showProjections, analysisTotalMetrics]);

    return (
        <AnalysisContext.Provider value={providerValue}>
            {children}
        </AnalysisContext.Provider>
    )
}
